import { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGrid, itIT, GridToolbar, useGridApiRef, gridFilteredSortedRowEntriesSelector } from '@mui/x-data-grid';		//https://mui.com/x/react-data-grid/ [MIT version]
import { Box, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, MenuItem } from '@mui/material';
import Separator from '../Separator.js';

import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import VerifiedIcon from '@mui/icons-material/Verified';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import PaymentsIcon from '@mui/icons-material/Payments';

import Api from '../../Services/Api';
import { PAGE_SIZE, pageSizeOptions, timeTableIntervals as optionsTimeTableIntervals, paymentsTypesEnum } from '../../Config/constants.js';
import moment from 'moment';
import { Link } from 'react-router-dom';
import ActionButton from '../ActionButton.js';

function ReportReceivedPaymentsList() {

	const apiRef = useGridApiRef();	//https://mui.com/x/api/data-grid/
	const DEFAULT_VALUE = "0";

	const [rows, setRows] = useState([]);
	const [totalCashIds, setTotalCashIds] = useState([]);
	const [totalPosIds, setTotalPosIds] = useState([]);
	const [displayAlert, setDisplayAlert] = useState(0);
	const [displayDialog, setDisplayDialog] = useState(0);
	const [lastUpdate, setLastUpdate] = useState(null);
	const [days, setDays] = useState(DEFAULT_VALUE);

	const columns = [
		{
			field: 'customer',
			headerName: 'Cliente',
			flex: 1,
			valueGetter: (params) => {
				return `${params.row.surname} ${params.row.name}`;
			},
		},
		{
			field: 'installation',
			headerName: 'Impianto',
			flex: 2,
			valueGetter: (params) => {
				return `${params.row.brand} ${params.row.model} ${params.row.unique_code}`;
			},
		},
		{
			field: 'appointment_at',
			headerName: 'Appuntamento',
			flex: 2,
			type: 'date',
			valueGetter: (params) => params.row.appointment_at ? new Date(params.row.appointment_at) : null,
			valueFormatter: (params) => params.value ? params.value.toLocaleDateString('it-IT') : '',
		},
		{
			field: 'payment_method',
			headerName: 'Tipo di pagamento',
			flex: 1,
		},
		{
			field: 'price',
			headerName: 'Importo',
			type: 'number',
			valueFormatter: (params) => params.value != null ? new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : '',
		},       
		{
			field: 'id',
			headerName: 'Azioni',
			flex: 1,
			renderCell: (params) => {
				function approvePayment(e) {
					e.stopPropagation(); // don't select this row after clicking
                    if (window.confirm(`Confermare l'incasso di ${params.row.price} euro tramite ${params.row.payment_method}?`)) {
						approvePayments([params.value])
					}
				};

				if(params.row.payment_approved) {
					return <>
						<ActionButton title={"Pagamento già approvato"} disabled={true}>
							<VerifiedIcon />
						</ActionButton>
					</>
				}
				return <>
					<ActionButton title={"Approva"} onClick={approvePayment}>
						<PriceCheckIcon />
					</ActionButton>
				</>
			}
		},
	]

	const handleClose = () => {
		setDisplayDialog(0);
	};

	function arrayIntervalsResponse() {
		return (
			[DEFAULT_VALUE, ...optionsTimeTableIntervals].map((interval) => (
				<MenuItem key={interval} value={interval}>
					{interval === '0' ? 'Oggi' : `${interval} giorni`}
				</MenuItem>
			))
		);
	}

	function extraFilter(selectedValue) {
		setDays(selectedValue);
	}

	function approvePayments(ids) {
		try {
			Api.post({endpoint: `report/approve-payment`, body: {"list": ids}, contentType : 'application/json'})
				.then(response => {
					setDisplayDialog(1);
					setLastUpdate(Date.now()+1);
				})
				.catch(error => {
					console.log(JSON.stringify(error));
					setDisplayDialog(-1);
				});
		} catch (error) {
			console.log(JSON.stringify(error));
			setDisplayDialog(-1);
		}
	};

	useEffect(() => {
		try {
			Api.get({endpoint: `report/received-payments/${days}`, contentType : 'application/json'})
			.then(response => {
				setRows(response.data.list);
				setTotalCashIds(response.data.totalCashIds)
				setTotalPosIds(response.data.totalPosIds)
			})
			.catch(error => {
				console.log(JSON.stringify(error));
				setDisplayAlert(-1);
			});
		} catch (error) {
			console.log(JSON.stringify(error));
			setDisplayAlert(-1);
		}
	}, [lastUpdate, days]);	// eslint-disable-line react-hooks/exhaustive-deps

	const theme = createTheme({
			palette: {
				primary: { main: '#11cb5f' },
			},
		},
		itIT,
	);

	const Totals = () => {
		const filteredSortedRows = gridFilteredSortedRowEntriesSelector(apiRef);

		const columnTotals = [
			{
				field: 'totalPosRows',
				headerName: 'Totale righe POS',
				flex: 1,
			},
			{
				field: 'totalPosPayments',
				headerName: 'Totale pagamenti con POS',
				valueGetter: (params) => {
					return params.row.totalPosPayments.toFixed(2);
				},
				flex: 1,
			},
			{
				field: 'totalCashRows',
				headerName: 'Total righe Contanti',
				flex: 1,
			},
			{
				field: 'totalCashPayments',
				headerName: 'Totale pagamenti in Contanti',
				valueGetter: (params) => {
					return params.row.totalCashPayments.toFixed(2);
				},
				flex: 1,
			},
			{
				field: 'totalPayments',
				headerName: 'Totale della giornata',
				valueGetter: (params) => {
					return params.row.totalPayments.toFixed(2);
				},
				flex: 1,
			},
			{
				field: 'id',
				headerName: 'Azioni',
				flex: 1,
				renderCell: (params) => {
					function approveAllPosPayments(e) {
						e.stopPropagation(); // don't select this row after clicking
						if (window.confirm(`Confermi di aver incassato un totale di ${params.row.totalPosToApprove} euro con POS?`)) {
							if (window.confirm("Sei davvero sicuro di voler confermare tutti i pagamenti con POS?")) {
								approvePayments(totalPosIds);
							}
						}
					};
					function approveAllCashPayments(e) {
						e.stopPropagation(); // don't select this row after clicking
						if (window.confirm(`Confermi di aver incassato un totale di ${params.row.totalCashToApprove} euro in Contanti?`)) {
							if (window.confirm("Sei davvero sicuro di voler confermare tutti i pagamenti in Contanti?")) {
								approvePayments(totalCashIds);
							}
						}
					};
					return <>
						<ActionButton title={"Approva tutti i pagamenti con POS"} onClick={approveAllPosPayments} disabled={totalPosIds.length < 1}>
							<AssuredWorkloadIcon />
						</ActionButton>
						<ActionButton title={"Approva tutti i pagamenti in Contanti"} onClick={approveAllCashPayments} disabled={totalCashIds.length < 1}>
							<PaymentsIcon />
						</ActionButton>
					</>
				}
			},
		];

		let rowTotalsData = {
			id: 1,
			totalPayments: 0,
			totalPosRows: 0,
			totalPosPayments: 0,
			totalPosToApprove: 0,
			totalCashRows: 0,
			totalCashPayments: 0,
			totalCashToApprove: 0,
		};
		filteredSortedRows.forEach((row) => {
			if(row.model.payment_method === paymentsTypesEnum.pos) {
				rowTotalsData.totalPosRows += 1;
				rowTotalsData.totalPosPayments += parseFloat(row.model.price);
				if(!row.model.payment_approved) {
					rowTotalsData.totalPosToApprove += parseFloat(row.model.price);
				}
			} else if(row.model.payment_method === paymentsTypesEnum.cash) {
				rowTotalsData.totalCashRows += 1;
				rowTotalsData.totalCashPayments += parseFloat(row.model.price);
				if(!row.model.payment_approved) {
					rowTotalsData.totalCashToApprove += parseFloat(row.model.price);
				}
			}
		});
		rowTotalsData.totalPayments = (rowTotalsData.totalPosPayments + rowTotalsData.totalCashPayments);

		return <>
			<Separator />
			<DataGrid
				sx={{ ".MuiDataGrid-footerContainer": { display: "none" }, marginTop: '0rem' }}
				rows={[rowTotalsData]}
				columns={columnTotals}				
			/>
		</>;
	};

	  
	return (
		<>
			<h5>Report Pagamenti giornalieri ricevuti</h5>

			<div className="row styled">
				<div className="col-md-2">
					<TextField
						fullWidth
						label="Intervallo di visualizzazione"
						name="interval"
						className="styled"
						select
						defaultValue = {DEFAULT_VALUE}
						InputProps={{
							style: {color: theme.palette.primary.darkMain}
						}}
						onChange={(e) => {
							extraFilter(e.target.value);
						}}
					>
						{arrayIntervalsResponse()}
					</TextField>
				</div>
				<div className="col-md-10">
					<p style={{fontSize: '0.8em'}}>
						{days && <span>Dal: {moment().subtract(days, 'days').format('DD/MM/YYYY')} - Al: {moment().format('DD/MM/YYYY')}</span>}
					</p>
				</div>
			</div>

			<Box sx={{ width: '100%', minWidth: '800px' }}>
				<ThemeProvider theme={theme}>
					<DataGrid
						sx={{ minHeight: '300px' }}
						rows={rows}
						columns={columns}						
						initialState={{
							pagination: {
								paginationModel: {
									pageSize: PAGE_SIZE,
								},
							},
						}}
						apiRef={apiRef}
						slots={{
							toolbar: GridToolbar,
							footer: Totals,
						}}
						slotProps={{
							columnsPanel: {
								disableHideAllButton: true,
							},
							toolbar: {
								showQuickFilter: true,
							},
						}}
						pageSizeOptions={pageSizeOptions}
					/>
					<div className="mt-2">
						<Link to={'/cms/reports'} >
							<Button variant="contained">Torna alla lista</Button>
						</Link>
					</div>
				</ThemeProvider>
				{displayAlert === -1 && (
					<Alert severity="error">Si è verificato un errore imprevisto. Riprovare più tardi</Alert>
				)}
			</Box>

			<Dialog
				open={displayDialog !== 0}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					{displayDialog === 1 && "Operazione completata"}
					{displayDialog === -1 && "Errore"}
				</DialogTitle>
				<DialogContent>
						<DialogContentText id="alert-dialog-description">
							{displayDialog === 1 && "Operazione completata con successo!"}
							{displayDialog === -1 && "Si è verificato un errore inatteso"}
						</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Chiudi</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default ReportReceivedPaymentsList;